import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Megaphone, Sparkles, Loader2 } from "lucide-react";
import { CampaignCard } from "@/components/marketing/CampaignCard";

const Toggle = ({ value, options, onChange, testId }) => (
  <div className="flex border border-black" data-testid={testId}>
    {options.map(([k, label]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
        style={value === k ? { color: "#fff" } : undefined}
        className={`flex-1 h-11 px-3 text-xs font-semibold uppercase tracking-widest transition-colors ${value === k ? "bg-black" : "bg-white hover:bg-zinc-100"}`}>
        {label}
      </button>
    ))}
  </div>
);

export default function MarketingPage() {
  const [items, setItems] = useState([]);
  const [goal, setGoal] = useState("clientes");
  const [platform, setPlatform] = useState("instagram");
  const [brief, setBrief] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => api.get("/marketing/campaigns").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const pending = items.some((c) => c.status === "text" || c.status === "image");
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [pending, load]);

  const generate = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/marketing/campaigns", { goal, platform, brief });
      setItems((xs) => [data, ...xs]);
      setBrief("");
      toast.success("Creando tu publicidad… tarda unos 30 segundos");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta publicidad?")) return;
    await api.delete(`/marketing/campaigns/${id}`).catch(() => {});
    setItems((xs) => xs.filter((c) => c.id !== id));
  };

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-6xl" data-testid="marketing-page">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Departamento</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
        <Megaphone className="text-[#E63946]" size={30} /> Marketing
      </h1>
      <p className="text-sm text-zinc-600 mt-2 mb-6 max-w-2xl">
        La IA crea la imagen con el logo, la descripción, los hashtags y la canción ideal para promocionar DS Travel Tours.
      </p>

      <div className="border-2 border-black bg-white p-5 space-y-4 mb-8" data-testid="marketing-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest mb-1">Objetivo</div>
            <Toggle value={goal} onChange={setGoal} testId="marketing-goal" options={[["clientes", "Atraer clientes"], ["conductores", "Captar conductores"]]} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest mb-1">Red social</div>
            <Toggle value={platform} onChange={setPlatform} testId="marketing-platform" options={[["instagram", "Instagram"], ["whatsapp", "WhatsApp Estados"]]} />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest mb-1">Idea o enfoque (opcional)</div>
          <Textarea rows={2} value={brief} onChange={(e) => setBrief(e.target.value)} maxLength={500}
            placeholder="Ej.: oferta de verano al aeropuerto, excursión a Toledo, buscamos conductores con minivan…"
            className="rounded-none border-black" data-testid="marketing-brief" />
        </div>
        <button onClick={generate} disabled={sending} data-testid="marketing-generate-btn"
          className="inline-flex items-center gap-2 h-11 px-6 bg-[#E63946] text-white text-xs font-semibold uppercase tracking-widest hover:bg-black transition-colors disabled:opacity-50">
          {sending ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} Crear publicidad
        </button>
      </div>

      <div className="space-y-5" data-testid="marketing-list">
        {items.length === 0 && <p className="text-sm text-zinc-500">Aún no hay publicidades. ¡Crea la primera!</p>}
        {items.map((c) => <CampaignCard key={c.id} c={c} onDelete={remove} />)}
      </div>
    </div>
  );
}
